import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import { 
  CalendarBlank, 
  Users, 
  Medal, 
  CreditCard,
  ArrowRight,
  Check
} from '@phosphor-icons/react';

const LandingPage = () => {
  const { user, t, language, setLanguage } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const features = [
    {
      icon: CalendarBlank,
      title: language === 'de' ? 'Einfache Buchung' : 'Easy Booking',
      description: language === 'de' 
        ? 'Buchen Sie Ihren Lieblingsplatz in Sekunden'
        : 'Book your favorite court in seconds'
    },
    {
      icon: Users,
      title: language === 'de' ? 'Offene Spiele' : 'Open Matches',
      description: language === 'de'
        ? 'Finden Sie Spielpartner auf Ihrem Level'
        : 'Find playing partners at your level'
    },
    {
      icon: Medal,
      title: language === 'de' ? 'Level-System' : 'Level System',
      description: language === 'de'
        ? 'Spielen Sie mit Gegnern Ihrer Spielstaerke'
        : 'Play with opponents matching your skill'
    },
    {
      icon: CreditCard,
      title: language === 'de' ? 'Guthaben & Gutscheine' : 'Credits & Vouchers',
      description: language === 'de'
        ? 'Flexibles Bezahlsystem mit Prepaid-Optionen'
        : 'Flexible payment with prepaid options'
    }
  ];

  const pricingFeatures = [
    language === 'de' ? '8 Indoor-Plätze' : '8 Indoor Courts',
    language === 'de' ? 'Wöchentliche Mixins & Turniere' : 'Weekly mixins & tournaments',
    language === 'de' ? 'Trainings für jedes Level' : 'Training sessions for every level',
    language === 'de' ? 'Junior-Akademie' : 'Junior academy',
    language === 'de' ? 'Spielpartner auf deinem Level' : 'Playing partners at your level',
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero Section - Keep dramatic with dark overlay */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1689942963385-f5bd03f3b270?auto=format&fit=crop&w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/60 to-slate-900/80" />
        </div>

        <header className="absolute top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src="/logo.png" alt="Padelarena Wädenswil" className="h-10 sm:h-12 w-auto flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-white font-bold text-sm sm:text-lg tracking-tight block truncate">Padelarena Wädenswil</span>
                <span className="text-emerald-400 text-[10px] sm:text-xs tracking-wider font-semibold hidden sm:block">Padlr. · Booking Platform</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <button
                onClick={() => setLanguage(language === 'en' ? 'de' : 'en')}
                className="text-white/70 hover:text-white text-sm font-medium"
              >
                {language === 'en' ? 'DE' : 'EN'}
              </button>
              <Link to="/login" className="text-white/80 hover:text-white font-medium text-sm">
                {t('login')}
              </Link>
              <Link to="/register" className="btn-primary text-xs sm:text-sm py-2 px-3 sm:px-5" data-testid="hero-register-btn">
                {t('register')}
              </Link>
            </div>
          </div>
        </header>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-emerald-400 font-semibold text-sm tracking-wider mb-4">
              {language === 'de' ? 'WILLKOMMEN BEI' : 'WELCOME TO'}
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tighter leading-[1.05]">
              Padelarena
              <br />
              <span className="text-emerald-400">Wädenswil</span>
            </h1>
            <p className="text-base sm:text-lg text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
              {language === 'de' 
                ? 'Die erste Adresse für Padel am Zürichsee. Buchen Sie Ihren Platz online und finden Sie Spielpartner auf Ihrem Level.'
                : 'The premier destination for padel at Lake Zurich. Book your court online and find playing partners at your level.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register" 
                className="btn-primary text-lg py-4 px-8 inline-flex items-center justify-center gap-2"
                data-testid="cta-register-btn"
              >
                {language === 'de' ? 'Jetzt starten' : 'Get Started'}
                <ArrowRight size={20} weight="bold" />
              </Link>
              <Link 
                to="/login" 
                className="bg-white/10 backdrop-blur-sm text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/20 transition-all text-lg"
                data-testid="cta-login-btn"
              >
                {language === 'de' ? 'Anmelden' : 'Sign In'}
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-emerald-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-emerald-600 font-semibold text-sm tracking-wider mb-3">FEATURES</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              {language === 'de' ? 'Alles was Sie brauchen' : 'Everything You Need'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-6"
              >
                <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon size={24} weight="duotone" className="text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-white" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-emerald-600 font-semibold text-sm tracking-wider mb-3">
              {language === 'de' ? 'MEHR ALS NUR EIN PLATZ' : 'MORE THAN JUST A COURT'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              {language === 'de' ? 'Das Padel-Center mit der besten Community' : 'The padel center with the best community'}
            </h2>
            <p className="text-slate-500 text-base sm:text-lg mt-4 max-w-2xl mx-auto">
              {language === 'de'
                ? 'Top-Trainer, wöchentliche Mixins, Turniere und eine Junior-Akademie — hier findest du deinen Platz.'
                : 'Top trainers, weekly mixins, tournaments and a junior academy — find your place to play.'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-static p-8 md:p-12"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="md:max-w-xs">
                <p className="text-slate-400 text-xs font-semibold tracking-wider uppercase mb-2">
                  {language === 'de' ? 'WAS DICH ERWARTET' : 'WHAT AWAITS YOU'}
                </p>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-4xl sm:text-5xl font-extrabold text-emerald-600">
                    {language === 'de' ? 'Community' : 'Community'}
                  </span>
                </div>
                <p className="text-slate-500 text-sm mt-3 leading-relaxed">
                  {language === 'de'
                    ? 'Ob Beginner oder Fortgeschritten — du findest hier deine Spielpartner, deinen Coach und deinen Mixing-Abend.'
                    : "Whether you're a beginner or advanced — you'll find your playing partners, your coach and your weekly mixing night."}
                </p>
              </div>

              <div className="flex-1 md:pl-8 md:border-l border-slate-200">
                <ul className="space-y-3">
                  {pricingFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3 text-slate-700">
                      <Check size={18} weight="bold" className="text-emerald-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-slate-200 text-center">
              <Link 
                to="/register" 
                className="btn-primary inline-flex items-center gap-2"
                data-testid="pricing-register-btn"
              >
                {language === 'de' ? 'Jetzt registrieren' : 'Register Now'}
                <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="cta-section">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1646649853703-7645147474ba?auto=format&fit=crop&w=1920&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-slate-900" />
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6">
              {language === 'de' 
                ? 'Bereit für Ihr nächstes Spiel?'
                : 'Ready for Your Next Game?'}
            </h2>
            <p className="text-lg text-white/70 mb-8">
              {language === 'de'
                ? 'Registrieren Sie sich jetzt und buchen Sie Ihren ersten Platz.'
                : 'Register now and book your first court.'}
            </p>
            <Link 
              to="/register" 
              className="btn-primary text-lg py-4 px-10 inline-flex items-center gap-2"
              data-testid="final-cta-btn"
            >
              {language === 'de' ? 'Jetzt starten' : 'Get Started'}
              <ArrowRight size={20} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-200 bg-slate-50" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pb-8 border-b border-slate-200">
            {/* Brand block */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="Padelarena Wädenswil" className="h-12 w-auto" />
                <div>
                  <span className="text-slate-900 font-bold text-lg block leading-tight">Padelarena Wädenswil</span>
                  <span className="text-emerald-600 text-xs font-semibold tracking-wider uppercase">Padel · Golf · Community</span>
                </div>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed">
                {language === 'de'
                  ? 'Der Schweizer Padel-Pionier am Zürichsee. 8 Indoor-Padel-Plätze, 2 Golf-Simulatoren, Junior & Adult Academy, Sports Bar mit 12 TVs.'
                  : 'The Swiss padel pioneer at Lake Zurich. 8 indoor padel courts, 2 golf simulators, Junior & Adult Academy, sports bar with 12 TVs.'}
              </p>
            </div>

            {/* Contact block */}
            <div className="space-y-2">
              <h4 className="text-slate-900 font-bold text-sm uppercase tracking-wider">
                {language === 'de' ? 'Kontakt' : 'Contact'}
              </h4>
              <p className="text-slate-600 text-sm">
                Zugerstrasse 162<br />
                8820 Wädenswil<br />
                {language === 'de' ? 'Schweiz' : 'Switzerland'}
              </p>
              <p className="text-slate-600 text-sm">
                <a href="tel:+41438330834" className="hover:text-emerald-700">+41 43 833 08 34</a><br />
                <a href="mailto:info@padelarena.ch" className="hover:text-emerald-700">info@padelarena.ch</a>
              </p>
            </div>

            {/* Opening hours block */}
            <div className="space-y-2">
              <h4 className="text-slate-900 font-bold text-sm uppercase tracking-wider">
                {language === 'de' ? 'Öffnungszeiten' : 'Opening Hours'}
              </h4>
              <p className="text-slate-600 text-sm">
                <span className="font-semibold text-slate-800">{language === 'de' ? 'Mo–Fr' : 'Mon–Fri'}</span> · 06:00 – 24:00<br />
                <span className="font-semibold text-slate-800">{language === 'de' ? 'Sa–So' : 'Sat–Sun'}</span> · 08:00 – 20:00
              </p>
              <a href="https://www.padelarena.ch" target="_blank" rel="noreferrer" className="inline-block text-emerald-600 hover:text-emerald-700 text-sm font-semibold mt-1">
                www.padelarena.ch →
              </a>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6">
            <p className="text-slate-400 text-xs">
              &copy; 2026 Padelarena Wädenswil. {language === 'de' ? 'Alle Rechte vorbehalten.' : 'All rights reserved.'} · {language === 'de' ? 'läuft auf' : 'powered by'} <strong className="text-emerald-600">Padlr.</strong>
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs">
              <Link to="/agb" className="text-slate-500 hover:text-emerald-700" data-testid="footer-agb">
                {language === 'de' ? 'AGB' : 'Terms'}
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/privacy" className="text-slate-500 hover:text-emerald-700" data-testid="footer-privacy">
                {language === 'de' ? 'Datenschutz' : 'Privacy'}
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/impressum" className="text-slate-500 hover:text-emerald-700" data-testid="footer-imprint">
                {language === 'de' ? 'Impressum' : 'Imprint'}
              </Link>
              <span className="text-slate-300">·</span>
              <a href="mailto:info@padelarena.ch" className="text-slate-500 hover:text-emerald-700" data-testid="footer-contact">
                info@padelarena.ch
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
